// Types
import { IndividualExternalExamType } from "src/services/exams/types";

// Constants
import { OCT_TYPE } from "src/constants/enums";

export type ExternalExamViewerType = "frames" | "image" | "file" | "none";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp"];

export const getFileUrlExtension = (url: string | null | undefined) => {
  if (!url) return "";

  const pathname = url.split(/[?#]/)[0];
  const fileName = pathname.substring(pathname.lastIndexOf("/") + 1);
  const dotIndex = fileName.lastIndexOf(".");

  return dotIndex === -1 ? "" : fileName.substring(dotIndex + 1).toLowerCase();
};

export const isPdfFileUrl = (url: string | null | undefined) =>
  getFileUrlExtension(url) === "pdf";

export const isImageFileUrl = (url: string | null | undefined) =>
  IMAGE_EXTENSIONS.includes(getFileUrlExtension(url));

export const resolveExternalExamViewer = (
  exam:
    | Pick<IndividualExternalExamType, "examType" | "examFileUrl" | "images">
    | null
    | undefined
): ExternalExamViewerType => {
  if (!exam) return "none";

  const isOct = Boolean(exam.examType?.includes(OCT_TYPE));

  if (isOct && (exam.images?.length ?? 0) > 0) return "frames";
  if (!exam.examFileUrl) return "none";
  if (isOct && isImageFileUrl(exam.examFileUrl)) return "image";

  return "file";
};

// The file lives on another origin, where a link's download attribute is
// ignored, so it's fetched as a blob first. If the fetch is blocked (e.g. by
// CORS), the file opens in a new tab instead so it can still be saved.
export const downloadExternalExamFile = async (
  fileUrl: string,
  fallbackFileName: string
) => {
  // Name the file after the last part of its path, without the query string
  const fileName =
    decodeURIComponent(
      new URL(fileUrl, window.location.href).pathname.split("/").pop() ?? ""
    ) || fallbackFileName;

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const objectUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error(error);
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  }
};
